import React, { useEffect, useState, useLayoutEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Trash2, Home, Eye, EyeOff } from "lucide-react";
import { normalizeType } from "../utils/normalizeType";
import { cleanReportName } from "../../utils/reportNameUtils";
import { updateReport, createSectionForReport, deleteReportSection, updateReportSectionState, updateReportSection } from "../../api/api";import pdfMake from "pdfmake/build/pdfmake";
import { vfs, fonts } from "../../fonts/pdfFonts";
import MakeDocDefinitionFromJson from "./MakeDocDefinitionFromJson";
import { downloadDocx } from "../pdf/makeDocxFromJson";
import imageCompression from "browser-image-compression";
import { PERMISSIONS } from "../../permissions/permissions";
import Can from "../../permissions/Can";
import { reportUrl } from "../utils/routeConstructor";
import { useReportTemplateContext } from "./ReportTemplate/ReportTemplateContext";
import { useRakennusData } from "../../context/RakennusDataContext";
import { usePTS } from "../../context/PTSContext";
import { SECTION_STATE_NORMALIZATION } from "../utils/reportingStatesNormalization";
import { SectionStateToggle } from "../utils/SectionStateToggle";
import { renderStatusBadge } from "../../pages/AllReports";
import { ReportStateToggle } from "../utils/ReportStateToggle";
import { format } from 'date-fns';
import { exportAllPTSToExcel } from "../utils/exportAllPTSToExcel";
import PdfReadyImagesChecker from "../utils/PdfReadyImagesChecker.jsx";
import { uploadReportToSharePoint } from "../utils/uploadToSharepoint";
import { imageUrlToBase64 } from "../hooks/emailAssets";
import waativaLogo from "../../assets/images/waativalogoteksilla.png";
import suomalainenLogo from "../../assets/images/Suomalaista_palvelua.png";
import '../../App.css';
import EmailModal from "../hooks/EmailModal";
import { buildEmailBody, buildEmailSubject } from "../hooks/emailTemplateService";
import { useUser } from "../../context/UserContext";
import ReportNotesPanel from "./ReportNotesPanel.jsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { getKiinteistoImagesByKiinteistoId } from "../../api/api";
import { mergePdfAttachments } from "../utils/mergePdfAttachments";
import { isSentinelSection } from "../../assets/subsectionBlockTemplates";

const sectionUrlKey = (s) =>
  /^section_chip_/.test(s?.type ?? '') ? normalizeType(s.name ?? '') : (s?.type ?? '');

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const compressImagesInDocDefinition = async (doc, maxDimension = 1200, quality = 0.8, setLoadingProgress) => {
  if (!doc) return doc;

  console.time("compressImages"); // START TIMER

  const images = [];
  const collectImages = (node) => {
    if (Array.isArray(node)) node.forEach(collectImages);
    else if (node && typeof node === "object") {
      if (node.image && typeof node.image === "string" && node.image.startsWith("data:image/")) {
        images.push(node);
      }
      Object.values(node).forEach(collectImages);
    }
  };
  collectImages(doc);

  for (let i = 0; i < images.length; i++) {
    const node = images[i];
    try {
      const blob = await fetch(node.image).then((r) => r.blob());
      const compressedBlob = await imageCompression(blob, {
        maxWidthOrHeight: maxDimension,
        initialQuality: quality,
        useWebWorker: true,
      });
      node.image = await blobToBase64(compressedBlob);
    } catch (err) {
      console.warn("Failed to compress an image in PDF:", err);
    }
    setLoadingProgress(Math.round(((i + 1) / images.length) * 100)); // update progress
  }

  console.timeEnd("compressImages"); // END TIMER
  return doc;
};




pdfMake.vfs = vfs;
pdfMake.fonts = fonts;

const EXPORT_DEFAULT_QUALITY = 80;
const EXPORT_DEFAULT_MAX_DIM = 1200;

// Defaults for PDF preview
const PREVIEW_DEFAULT_QUALITY = 40;
const PREVIEW_DEFAULT_MAX_DIM = 300;


export default function ReportSidebar() {

  const { report, editMode, setEditMode, rakennusData, riskidata, fetchReportData: fetchData, fetchRiskidata: fetchLatestRiskidata, groupedToimenpiteet, assignments, mergedRakennusData, extractMeaningfulReportData, activeSubsectionId, sections: ctxSections, updateSection } = useReportTemplateContext();

  const [imageDumpLoading, setImageDumpLoading] = useState(false);

  const handleDownloadImages = async () => {
    const kiinteistoId = rakennusData?.id_kiinteisto || mergedRakennusData?.id_kiinteisto;
    if (!kiinteistoId) return;
    setImageDumpLoading(true);
    try {
      const { data } = await getKiinteistoImagesByKiinteistoId(kiinteistoId);
      const images = data?.rows || data || [];
      const zip = new JSZip();
      const azureBase = process.env.REACT_APP_AZURE_BLOB_BASE;
      const sanitize = s => (s || '').replace(/[^\wäöåÄÖÅ \-]/g, '').trim().replace(/\s+/g, '_').slice(0, 60);

      for (const img of images) {
        const category = sanitize(img.metadata?.category || img.category || '');
        const subCategory = sanitize(img.metadata?.subCategory || img.subCategory || '');
        const description = sanitize(img.metadata?.description || '');
        const url = img.sasUrl || (azureBase ? `${azureBase}/${img.azure_blob_name}` : null);
        if (!url) continue;
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const blob = await res.blob();
          const ext = (img.azure_blob_name || '').split('.').pop().split('?')[0] || 'jpg';
          const catPart = [category, subCategory].filter(Boolean).join('_');
          const filename = `${[catPart, description].filter(Boolean).join('-') || img.id}.${ext}`;
          zip.file(filename, blob);
        } catch { /* skip failed images */ }
      }

      const kohteenNimi = rakennusData?.kohteen_nimi || mergedRakennusData?.kohteen_nimi || '';
      const zipNameParts = [kohteenNimi, report?.name].filter(Boolean);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${zipNameParts.join('_') || 'kuvat'}_images.zip`);
    } catch (err) {
      console.error('Image dump failed:', err);
    } finally {
      setImageDumpLoading(false);
    }
  };

  const handleDownloadJson = async () => {
    try {
      const data = await extractMeaningfulReportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const kohteenNimi = rakennusData?.kohteen_nimi || mergedRakennusData?.kohteen_nimi || '';
      const nameParts = [kohteenNimi, report?.name].filter(Boolean);
      a.download = `${nameParts.join('_') || 'raportti'}_llm.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('JSON export failed:', err);
    }
  };

const getSavedContactEmail = () => {
  for (const section of report?.report_sections || []) {
    try {
      const content = JSON.parse(section.content || "{}");
  

      for (const block of content.blocks || []) {
        if (block.type !== "ToimeksiantajaBlock") continue;

        const value = block.data?.value || "";
        const line = value
          .split("\n")
          .find((row) => row.startsWith("contactEmail:"));

        return line?.replace("contactEmail:", "").trim() || "";
      }
    } catch {
      // ignore invalid section content
    }
  }

  return "";
};
const getHiddenSectionIds = (allSections) => {
    for (const sec of allSections) {
      if (!sec.content) continue;
      try {
        const parsed = JSON.parse(sec.content);
        const block = (parsed.blocks ?? []).find(b => b.type === 'SectionSelectionBlock');
        if (block?.data?.hiddenSectionIds?.length) return new Set(block.data.hiddenSectionIds.map(Number));
      } catch {}
    }
    return new Set();
  };

  // Group all relevant data into a single variable
  const ptsContext = usePTS();

  const { id: kiinteistoId, templateName, section, sectionId } = useParams();

  const navigate = useNavigate();
  const [sidebarTitle, setSidebarTitle] = useState("");
  const [sections, setSections] = useState([]);
  const [subsectionReadiness, setSubsectionReadiness] = useState({});
  // sectionHidden: sectionId -> bool, subsectionHidden: blockId -> bool.
  // "Hidden" means excluded from PDF export (see MakeDocDefinitionFromJson)
  // but still shown here, dimmed, so it stays easy to find and un-hide —
  // same soft-toggle shape as julkisivu v2's tarkastuskohde.is_active.
  const [sectionHidden, setSectionHidden] = useState({});
  const [subsectionHidden, setSubsectionHidden] = useState({});
  const [newSectionName, setNewSectionName] = useState("");
  const [addingSection, setAddingSection] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [loadingDocx, setLoadingDocx] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [pdfError, setPdfError] = useState(null);


  const [showPdfSettingsModal, setShowPdfSettingsModal] = useState(false);
  const [compressPDF, setCompressPDF] = useState(true);
  const [pdfQuality, setPdfQuality] = useState(PREVIEW_DEFAULT_QUALITY);
  const [pdfMaxDimension, setPdfMaxDimension] = useState(PREVIEW_DEFAULT_MAX_DIM);
  const { meUser } = useUser();
  const homeRedirect = !kiinteistoId ? "/raportit" : `/taloyhtiokortti/${kiinteistoId}`;
  const isTemplateView = !kiinteistoId;
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [pdfSettingsNextAction, setPdfSettingsNextAction] = useState("export"); // default to "export"
  const [loadingProgress, setLoadingProgress] = useState(0); // 0–100
  const [showPreviewImages, setShowPreviewImages] = useState(false);
const [emailInitialRecipients, setEmailInitialRecipients] = useState([]);
const [emailSubject, setEmailSubject] = useState("");
const [emailBody, setEmailBody] = useState("");
const [emailPdfFilename, setEmailPdfFilename] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);


useLayoutEffect(() => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "instant" 
  });
}, [sectionId]);




  useEffect(() => {
    setSidebarTitle(report?.name || "Raportti");
    setNameInput(report?.name || "");
    let reportSections = Array.isArray(report?.report_sections) ? [...report.report_sections] : [];
    if (Array.isArray(report?.section_order)) {
      reportSections.sort((a, b) => report.section_order.indexOf(a.id) - report.section_order.indexOf(b.id));
    }
    setSections(reportSections);
    // Merge server state into the readiness/hidden maps, but local optimistic updates win
    const readinessMap = {};
    const subHiddenMap = {};
    const secHiddenMap = {};
    for (const s of reportSections) {
      try {
        const parsed = JSON.parse(s.content || '{}');
        Object.assign(readinessMap, parsed.subsectionStates ?? {});
        Object.assign(subHiddenMap, parsed.subsectionHidden ?? {});
        if (parsed.sectionHidden) secHiddenMap[s.id] = true;
      } catch {}
    }
    setSubsectionReadiness(prev => ({ ...readinessMap, ...prev }));
    setSubsectionHidden(prev => ({ ...subHiddenMap, ...prev }));
    setSectionHidden(prev => ({ ...secHiddenMap, ...prev }));
    // setOriginalSections(reportSections.filter((s) => s.template !== false));
  }, [report]); 

  const handleClickSection = (sectionType, sectionId) => {
    if (!report?.id) {
      console.error("Report ID missing for navigation");
      return;
    }

    navigate(
      reportUrl({
        kiinteistoId,
        templateName,
        reportId: report.id,
        section: sectionType,
        sectionId: sectionId
      })
    );
    setMobileOpen(false);
  };


  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === report?.name || savingName) return;
    setSavingName(true);
    try {
      await updateReport(report.id, { name: trimmed });
      await fetchData();
    } catch (error) {
      console.error("Failed to rename report:", error);
    } finally {
      setSavingName(false);
    }
  };

  const handleAddSectionClick = () => setAddingSection(true);

  const handleCreateSection = async (e) => {
    e.preventDefault();
    if (!newSectionName.trim()) return;

    const type = normalizeType(newSectionName);
    try {
      const created = await createSectionForReport({
        report_id: report.id,
        userId: 15,
        data: { name: newSectionName.trim(), type, content: "", template: false },
      });
      const newSections = [...sections, created.data];
      setSections(newSections);
      setNewSectionName("");
      setAddingSection(false);
      await updateReport(report.id, { section_order: newSections.map((s) => s.id) });
    } catch (error) {
      console.error("Failed to create section:", error);
    }
  };

  const handleDeleteSection = async (id, name) => {
    if (!window.confirm(`Haluatko varmasti poistaa osion "${name}"? Tämä poistaa myös sen aliosiot.`)) return;
    try {
      await deleteReportSection(id);
      const newSections = sections.filter((s) => s.id !== id);
      setSections(newSections);
      await updateReport(report.id, { section_order: newSections.map((s) => s.id) });
    } catch (error) {
      console.error("Failed to delete section:", error);
    }
  };

  const retryWithLoading = async (taskFn, setLoadingMessage, maxAttempts = 3, loadingText = "Luodaan PDF…") => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      setLoadingMessage(`${loadingText}`);
      try {
        return await taskFn(); // success, return result
      } catch (err) {
        console.error(`${loadingText} epäonnistui yrityksellä ${attempt}:`, err);
        if (attempt === maxAttempts) throw err; // fail after max attempts
      }
    }
  };

  const handlePreviewPDF = async () => {
    setLoadingPDF(true);
    setLoadingProgress(0);

    try {
      await retryWithLoading(async () => {
        setLoadingProgress(0);
        const latestRiskidata = await fetchLatestRiskidata();
        setLoadingProgress(5);

        const onProgress = (pct, msg) => {
          setLoadingProgress(pct);
          if (msg) setLoadingMessage(msg);
        };

        const { docDefinition, pdfAttachments } = await MakeDocDefinitionFromJson({
          report,
          rakennusData,
          mergedRakennusData,
          assignments,
          riskidata: latestRiskidata,
          isPreview: true,
          showPreviewImages,
          groupedToimenpiteet,
          ptsContext,
          onProgress,
        });

        setLoadingProgress(90);
        setLoadingMessage("Rendataan PDF…");

        const pdfBytes = await new Promise((resolve) =>
          pdfMake.createPdf(docDefinition).getBuffer(resolve)
        );
        const mergedBytes = await mergePdfAttachments(pdfBytes, pdfAttachments);

        setLoadingProgress(100);
        const blobUrl = URL.createObjectURL(new Blob([mergedBytes], { type: 'application/pdf' }));
        window.open(blobUrl, "_blank");
      }, setLoadingMessage, 3, "Luodaan esikatselua…");
    } catch (err) {
      setPdfError({ context: "Esikatselun luonti epäonnistui 3 yrityksellä.", err });
    } finally {
      setLoadingPDF(false);
    }
  };

const handleExportPDF = async () => {
  setLoadingPDF(true);
  setLoadingProgress(0);

  try {
    setPdfQuality(EXPORT_DEFAULT_QUALITY);
    setPdfMaxDimension(EXPORT_DEFAULT_MAX_DIM);

    await retryWithLoading(async () => {
      setLoadingProgress(0);
      const latestRiskidata = await fetchLatestRiskidata();
      setLoadingProgress(5);

      const onProgress = (pct, msg) => {
        setLoadingProgress(pct);
        if (msg) setLoadingMessage(msg);
      };

      const { docDefinition, pdfAttachments } = await MakeDocDefinitionFromJson({
        report: report,
        rakennusData,
        mergedRakennusData,
        assignments,
        riskidata: latestRiskidata,
        groupedToimenpiteet,
        ptsContext,
        quality: compressPDF ? pdfQuality / 100 : 1,
        dimensions: compressPDF ? pdfMaxDimension : null,
        onProgress,
      });

      setLoadingProgress(90);
      setLoadingMessage("Rendataan PDF…");

      const filename = `${cleanReportName(report?.name) || "Raportti"}_${
        rakennusData.kohteen_nimi || rakennusData?.rakennukset_fulls?.[0]?.osoite || ""
      }.pdf`;
      const pdfBytes = await new Promise((resolve) =>
        pdfMake.createPdf(docDefinition).getBuffer(resolve)
      );
      const mergedBytes = await mergePdfAttachments(pdfBytes, pdfAttachments);
      saveAs(new Blob([mergedBytes], { type: 'application/pdf' }), filename);
      setLoadingProgress(100);
    }, setLoadingMessage, 3, "Luodaan PDF-tiedostoa…");
  } catch (err) {
    console.error("PDF export failed:", err);
    setPdfError({ context: "PDF:n luonti epäonnistui 3 yrityksellä.", err });
  } finally {
    setLoadingPDF(false);
  }
};
   
  const handleExportDocx = async () => {
    setLoadingDocx(true);
    setLoadingMessage("Luodaan Word-tiedostoa…");
    try {
      const latestRiskidata = await fetchLatestRiskidata();
      const filename = `${cleanReportName(report?.name) || "Raportti"}_${
        rakennusData?.kohteen_nimi || rakennusData?.rakennukset_fulls?.[0]?.osoite || ""
      }.docx`;
      await downloadDocx({ report, rakennusData, mergedRakennusData, riskidata: latestRiskidata, assignments, groupedToimenpiteet, ptsContext }, filename);
    } catch (err) {
      console.error("DOCX export failed:", err);
      alert("Word-tiedoston luonti epäonnistui.");
    } finally {
      setLoadingDocx(false);
      setLoadingMessage("");
    }
  };

  const toggleSubsectionReadiness = async (sectionId, blockId) => {
    const ctxSection = ctxSections?.find(s => s.id === sectionId);
    const section = ctxSection || sections.find(s => s.id === sectionId);
    if (!section) return;
    let parsed;
    try { parsed = JSON.parse(section.content || '{}'); } catch { parsed = {}; }
    const current = subsectionReadiness[blockId] ?? (parsed.subsectionStates ?? {})[blockId];
    const newReadiness = current === 'ready' ? 'not_started' : 'ready';
    // Optimistic update — no flicker
    const newStates = { ...(parsed.subsectionStates ?? {}), [blockId]: newReadiness };
    setSubsectionReadiness(prev => ({ ...prev, [blockId]: newReadiness }));
    const updatedContent = JSON.stringify({ ...parsed, subsectionStates: newStates });
    updateSection?.({ ...section, content: updatedContent });

    // Auto-promote parent section to ready if all subsections are now ready
    const allSubIds = (parsed.blocks || [])
      .filter(b => b.type === 'subsection' || b.type === 'subsection2')
      .map(b => b.id)
      .filter(Boolean);
    const allReady = allSubIds.length > 0 && allSubIds.every(id => newStates[id] === 'ready');
    if (allReady && section.state !== 'ready') {
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content: updatedContent, state: 'ready' } : s));
      updateReportSectionState(sectionId, 'ready').catch(console.error);
    } else {
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content: updatedContent } : s));
    }

    try { await updateReportSection(section.id, { content: updatedContent }); }
    catch (err) {
      setSubsectionReadiness(prev => ({ ...prev, [blockId]: current }));
      console.error('Failed to save subsection readiness', err);
    }
  };

  // Hidden = excluded from PDF export (MakeDocDefinitionFromJson filters on
  // this), but stays visible (dimmed) here so it's easy to find and restore.
  // Same persistence shape as toggleSubsectionReadiness above — a flag
  // stored in the section's own content JSON, saved via updateReportSection.
  const toggleSectionHidden = async (sectionId) => {
    const ctxSection = ctxSections?.find(s => s.id === sectionId);
    const section = ctxSection || sections.find(s => s.id === sectionId);
    if (!section) return;
    let parsed;
    try { parsed = JSON.parse(section.content || '{}'); } catch { parsed = {}; }
    const current = sectionHidden[sectionId] ?? !!parsed.sectionHidden;
    const next = !current;
    setSectionHidden(prev => ({ ...prev, [sectionId]: next }));
    const updatedContent = JSON.stringify({ ...parsed, sectionHidden: next });
    updateSection?.({ ...section, content: updatedContent });
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content: updatedContent } : s));

    try { await updateReportSection(section.id, { content: updatedContent }); }
    catch (err) {
      setSectionHidden(prev => ({ ...prev, [sectionId]: current }));
      console.error('Failed to save section hidden state', err);
    }
  };

  const toggleSubsectionHidden = async (sectionId, blockId) => {
    const ctxSection = ctxSections?.find(s => s.id === sectionId);
    const section = ctxSection || sections.find(s => s.id === sectionId);
    if (!section) return;
    let parsed;
    try { parsed = JSON.parse(section.content || '{}'); } catch { parsed = {}; }
    const current = subsectionHidden[blockId] ?? !!(parsed.subsectionHidden ?? {})[blockId];
    const next = !current;
    const newHidden = { ...(parsed.subsectionHidden ?? {}), [blockId]: next };
    setSubsectionHidden(prev => ({ ...prev, [blockId]: next }));
    const updatedContent = JSON.stringify({ ...parsed, subsectionHidden: newHidden });
    updateSection?.({ ...section, content: updatedContent });
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, content: updatedContent } : s));

    try { await updateReportSection(section.id, { content: updatedContent }); }
    catch (err) {
      setSubsectionHidden(prev => ({ ...prev, [blockId]: current }));
      console.error('Failed to save subsection hidden state', err);
    }
  };

  const renderSubsections = (blocks = [], parentSection = null) => {
    const subsections = blocks.filter((b) => b.type === 'subsection' || b.type === 'subsection2');
    if (!subsections.length) return null;
    return (
      <div style={{ marginLeft: 12, paddingLeft: 0, paddingBottom: 4, paddingTop: 2 }}>
        {subsections.map((block, idx) => {
          const subsectionId = block.id || Math.random();
          const isActiveSub = activeSubsectionId === block.id;
          const title = block.data?.title || block.name || '—';
          const readiness = subsectionReadiness[block.id];
          const isSubHidden = subsectionHidden[block.id] ?? false;
          return (
            <div
              key={subsectionId}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  reportUrl({ kiinteistoId, templateName, reportId: report.id, section: sectionUrlKey(parentSection), sectionId: parentSection?.id, subsectionId: block.id })
                );
                setMobileOpen(false);
              }}
              style={{
                cursor: 'pointer',
                padding: '5px 10px 5px 12px',
                borderTop: idx > 0 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                borderLeft: `3px solid ${isActiveSub ? '#04aa00' : '#c8e6c8'}`,
                fontSize: '0.77rem',
                fontWeight: isActiveSub ? 600 : 400,
                color: isActiveSub ? '#04aa00' : '#444',
                backgroundColor: isActiveSub ? 'rgba(4,170,0,0.08)' : 'rgba(0,0,0,0.015)',
                transition: 'background-color 0.12s, color 0.12s',
                lineHeight: 1.4,
              }}
              onMouseEnter={e => { if (!isActiveSub) { e.currentTarget.style.backgroundColor = 'rgba(4,170,0,0.15)'; e.currentTarget.style.color = '#1a5c1a'; } }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = isActiveSub ? 'rgba(4,170,0,0.08)' : 'rgba(0,0,0,0.015)'; e.currentTarget.style.color = isActiveSub ? '#04aa00' : '#444'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, flexGrow: 1, minWidth: 0,
                  filter: isSubHidden ? 'blur(0.4px)' : 'none', opacity: isSubHidden ? 0.5 : 1,
                  transition: 'filter 0.15s ease, opacity 0.15s ease',
                }}>
                  {!isTemplateView && (
                    <span
                      onClick={e => { e.stopPropagation(); toggleSubsectionReadiness(parentSection?.id, block.id); }}
                      title={readiness === 'ready' ? 'Merkitse keskeneräiseksi' : 'Merkitse valmiiksi'}
                      style={{ flexShrink: 0, lineHeight: 1, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', transition: 'transform 0.15s, opacity 0.15s', opacity: 0.85 }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.25)'; e.currentTarget.style.opacity = '1'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = '0.85'; }}
                      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.95)'; }}
                      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1.25)'; }}
                    >
                      {readiness === 'ready'
                        ? <i className="bi bi-shield-fill-check text-success" style={{ fontSize: '0.85rem' }} />
                        : <i className="bi bi-shield-fill-exclamation text-warning" style={{ fontSize: '0.85rem' }} />
                      }
                    </span>
                  )}
                  <span style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                </div>
                {!isTemplateView && (
                  <span
                    onClick={e => { e.stopPropagation(); toggleSubsectionHidden(parentSection?.id, block.id); }}
                    title={isSubHidden ? 'Näytä osa PDF:ssä' : 'Piilota osa PDF:stä'}
                    style={{ flexShrink: 0, lineHeight: 1, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', transition: 'transform 0.15s, opacity 0.15s', opacity: 0.85, color: isSubHidden ? '#adb5bd' : '#6c757d' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.25)'; e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = '0.85'; }}
                    onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.95)'; }}
                    onMouseUp={e => { e.currentTarget.style.transform = 'scale(1.25)'; }}
                  >
                    {isSubHidden ? <EyeOff size={13} /> : <Eye size={13} />}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  


const handleUploadToSharePoint = () => {
  uploadReportToSharePoint({
    report,
    rakennusData,
    mergedRakennusData,
    assignments,
    ptsContext,
    groupedToimenpiteet,
    fetchLatestRiskidata,
    MakeDocDefinitionFromJson,
    setLoadingPDF,
    setLoadingMessage,
    setLoadingProgress
  });
};
  const moveSection = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sections.length) return;

    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [
      newSections[newIndex],
      newSections[index],
    ];

    setSections(newSections);

    if (report?.id) {
      try {
        await updateReport(report.id, {
          section_order: newSections.map(s => s.id),
        });
      } catch (err) {
        console.error("Failed to reorder sections:", err);
      }
    }
  };
const [showEmailModal, setShowEmailModal] = useState(false);

const [pdfBase64, setPdfBase64] = useState(null);


const handlePrepareEmail = async () => {
  try {
    setLoadingPDF(true);

    const latestRiskidata = await fetchLatestRiskidata();

    const { docDefinition, pdfAttachments } = await MakeDocDefinitionFromJson({
      report,
      rakennusData,
      mergedRakennusData,
      assignments,
      riskidata: latestRiskidata,
      groupedToimenpiteet,
      ptsContext
    });

    const pdfBytes = await new Promise((resolve) =>
      pdfMake.createPdf(docDefinition).getBuffer(resolve)
    );
    const mergedBytes = await mergePdfAttachments(pdfBytes, pdfAttachments);
    const base64 = btoa(String.fromCharCode(...new Uint8Array(mergedBytes)));
const filename = `${cleanReportName(report?.name) || "Raportti"}_${
  rakennusData?.kohteen_nimi ||
  rakennusData?.rakennukset_fulls?.[0]?.osoite ||
  ""
}.pdf`;

setEmailPdfFilename(filename);
    setPdfBase64(base64);

const defaultEmail =
  getSavedContactEmail() ||
  rakennusData?.isannoitsija_email ||
  "";

setEmailInitialRecipients(
  defaultEmail ? [defaultEmail] : []
);

setEmailSubject(
  buildEmailSubject(report, rakennusData)
);

const waativaLogoBase64 = await imageUrlToBase64(waativaLogo);
const suomalainenLogoBase64 = await imageUrlToBase64(suomalainenLogo);

setEmailBody(
  buildEmailBody(meUser, {
    waativaLogoBase64,
    suomalainenLogoBase64
  })
);

console.log(meUser);
    setShowEmailModal(true);

  } catch (err) {
    console.error(err);
    alert("❌ Virhe PDF:n luonnissa");
  } finally {
    setLoadingPDF(false);
  }
};
  const safeFilenamePart = (value) => {
    return String(value || "")
      .trim()
      .replace(/[\/\\:*?"<>|]/g, "")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const ptsExcelFilename = `PTS_${
    safeFilenamePart(
      rakennusData?.kohteen_nimi ||
      rakennusData?.rakennukset_fulls?.[0]?.osoite ||
      "osoite_puuttuu"
    )
  }_${format(new Date(), "yyyy-MM-dd")}.xlsx`;


  const renderSidebar = () => {
    const hiddenIds = isTemplateView ? new Set() : getHiddenSectionIds(sections);
    const visibleSections = (hiddenIds.size ? sections.filter(s => !hiddenIds.has(Number(s.id))) : sections)
      .filter(s => isTemplateView || !isSentinelSection(s));

    // Compute section numbers matching PDF logic (cover=0, TOC reserved at 2)
    const sectionNumMap = {};
    let secCounter = 0;
    let tocInserted = false;
    visibleSections.forEach((s, i) => {
      const isCover = (s.name || '').toLowerCase() === 'kansisivu';
      if (!isCover) {
        secCounter++;
        if (secCounter === 2 && tocInserted) secCounter++;
        sectionNumMap[s.id] = secCounter;
      }
      if (i === 1) tocInserted = true;
    });

    return (
      <div style={{ padding: '4px 0' }}>
        {visibleSections.map((s, index) => {
          const isActive = section === sectionUrlKey(s);
          let sectionBlocks = [];
          try { sectionBlocks = (s.content ? JSON.parse(s.content).blocks : []) || []; } catch {}
          const isSecHidden = sectionHidden[s.id] ?? false;
          // Structural sections (currently just the cover page) can't be
          // meaningfully excluded from the PDF — same "kansisivu" name
          // check MakeDocDefinitionFromJson already uses to special-case it.
          const isStructuralSection = (s.name || '').toLowerCase() === 'kansisivu';

          return (
            <div
              key={s.id}
              style={{
                borderLeft: `3px solid ${isActive ? '#04aa00' : 'transparent'}`,
                backgroundColor: isActive ? 'rgba(4,170,0,0.055)' : 'transparent',
                marginBottom: 1,
                transition: 'background-color 0.12s',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(4,170,0,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = isActive ? 'rgba(4,170,0,0.055)' : 'transparent'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '5px 8px 5px 9px' }}>

                {/* Edit mode: reorder arrows */}
                {editMode && (
                  <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0, marginRight: 2 }}>
                    <button
                      onClick={() => moveSection(index, -1)}
                      disabled={index === 0}
                      title="Siirrä ylös"
                      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? '#ccc' : '#888', lineHeight: 1 }}
                    >
                      <i className="bi bi-chevron-up" style={{ fontSize: '0.65rem' }} />
                    </button>
                    <button
                      onClick={() => moveSection(index, 1)}
                      disabled={index >= visibleSections.length - 1}
                      title="Siirrä alas"
                      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: index >= visibleSections.length - 1 ? 'default' : 'pointer', color: index >= visibleSections.length - 1 ? '#ccc' : '#888', lineHeight: 1 }}
                    >
                      <i className="bi bi-chevron-down" style={{ fontSize: '0.65rem' }} />
                    </button>
                  </div>
                )}

                <div style={{
                  display: 'flex', alignItems: 'center', gap: 3, flexGrow: 1, minWidth: 0,
                  filter: isSecHidden ? 'blur(0.4px)' : 'none', opacity: isSecHidden ? 0.5 : 1,
                  transition: 'filter 0.15s ease, opacity 0.15s ease',
                }}>
                  {/* Readiness badge */}
                  {!isTemplateView && (
                    <div style={{ flexShrink: 0 }}>
                      <SectionStateToggle key={s.state} section={s} requireConfirm={false} onStateChange={() => {}} />
                    </div>
                  )}

                  {/* Section title */}
                  <span
                    onClick={() => { handleClickSection(sectionUrlKey(s), s.id); }}
                    style={{
                      flexGrow: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      fontSize: '0.83rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#04aa00' : '#1e2023',
                      lineHeight: 1.4,
                      letterSpacing: isActive ? '-0.01em' : undefined,
                    }}
                  >
                    {sectionNumMap[s.id] ? `${sectionNumMap[s.id]}. ` : ''}{s.name || s.label || '—'}
                  </span>
                </div>

                {/* Hide/show in PDF */}
                {!isTemplateView && !isStructuralSection && (
                  <button
                    onClick={() => toggleSectionHidden(s.id)}
                    title={isSecHidden ? 'Näytä osio PDF:ssä' : 'Piilota osio PDF:stä'}
                    style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', color: isSecHidden ? '#adb5bd' : '#6c757d', flexShrink: 0, opacity: 0.75 }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '0.75'}
                  >
                    {isSecHidden ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                )}

                {/* Delete */}
                {editMode && (
                  <button
                    onClick={() => handleDeleteSection(s.id, s.name)}
                    title="Poista osio"
                    style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', color: '#c0392b', flexShrink: 0, opacity: 0.7 }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>

              {/* Subsections */}
              {sectionBlocks.length > 0 && renderSubsections(sectionBlocks, s)}
            </div>
          );
        })}
      </div>
    );
  };


const renderBottomButtons = () => (
  <div className="mt-2 border-top pt-2 d-flex flex-column gap-2" style={{ flexShrink: 0, paddingBottom: "0.5rem", paddingLeft: "0.5rem", paddingRight: "0.5rem" }}>
    {addingSection ? (
      <form onSubmit={handleCreateSection} className="d-flex gap-2">
        <input
          type="text"
          className="form-control"
          placeholder="Uuden osion nimi"
          value={newSectionName}
          onChange={(e) => setNewSectionName(e.target.value)}
          autoFocus
        />
        <button type="submit" className="btn btn-success">
          <i className="bi bi-check-lg"></i>
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setAddingSection(false)}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </form>
    ) : editMode ? (
      <button
        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center gap-2"
        onClick={handleAddSectionClick}
      >
        <i className="bi bi-plus-circle"></i>
        Lisää osio
      </button>
    ) : null}

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <button
        className="btn btn-outline-success text-info d-flex align-items-center justify-content-center gap-1"
        onClick={() => {
          setPdfSettingsNextAction("preview");
          setPdfQuality(PREVIEW_DEFAULT_QUALITY);
          setPdfMaxDimension(PREVIEW_DEFAULT_MAX_DIM);
          setShowPdfSettingsModal(true);
        }}
      >
        <i className="bi bi-eye"></i>
        Esikatsele
      </button>

      <button
        className="btn btn-outline-success text-danger d-flex align-items-center justify-content-center gap-1"
        onClick={() => {
          setPdfSettingsNextAction("export");
          setPdfQuality(EXPORT_DEFAULT_QUALITY);
          setPdfMaxDimension(EXPORT_DEFAULT_MAX_DIM);
          setShowPdfSettingsModal(true);
        }}
      >
        <i className="bi bi-file-earmark-pdf"></i>
        Lataa PDF
      </button>

      <button
        className="btn btn-success d-flex align-items-center justify-content-center gap-1"
        onClick={handleUploadToSharePoint}
        title="Tallenna Sharepointiin"
      >
        <i className="bi bi-cloud-upload"></i>
        SharePoint
      </button>

      <button
        className="btn btn-primary d-flex align-items-center justify-content-center gap-1"
        onClick={handlePrepareEmail}
      >
        <i className="bi bi-envelope"></i>
        Lähetä
      </button>
    </div>

    <div className="d-flex align-items-center justify-content-center">
      <ReportStateToggle
        report={report}
        onStateChange={(id, newState) => {
          console.log("onstate change report");
        }}
        requireConfirm={true}
      />
    </div>
  </div>
);
  return (
    <>
      {/* Mobile Hamburger */}
      <button
        className="btn btn-success d-md-none mb-2"
        style={{
          position: "fixed",
          top: 4,
          left: 4,
          zIndex: 1000,
          fontSize: "1.5rem",
          padding: "0.75rem 1rem",
          borderRadius: "0.5rem",
          width: "30px",
          height: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        <i className="bi bi-list" style={{ fontSize: "1.25rem" }}></i>
      </button>

      {/* Notes button — top-right, always visible */}
      {report && (
        <button
          className="btn btn-success"
          style={{
            position: "fixed",
            top: 4,
            right: 4,
            zIndex: 1000,
            width: "30px",
            height: "30px",
            borderRadius: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
          onClick={() => setNotesOpen(true)}
          title="Muistiinpanot"
        >
          <i className="bi bi-journal-text" style={{ fontSize: "1rem" }}></i>
        </button>
      )}

      {notesOpen && report && (
        <ReportNotesPanel
          reportId={report.id}
          onClose={() => setNotesOpen(false)}
        />
      )}

{/* Mobile sidebar */}
<div
  className="d-md-none"
  style={{
    position: "fixed",
    top: 0,
    left: 0,
    width: "80%",
    maxWidth: 280,
    height: "100dvh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    zIndex: 1100,
    boxShadow: "2px 0 8px rgba(0,0,0,0.3)",
    padding: "0.5rem",
    transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
    transition: "transform 0.22s ease",
    visibility: mobileOpen ? "visible" : "hidden",
  }}
>
    {/* Compact action row: close, home, cog */}
    <div className="d-flex align-items-center gap-1 mb-2 bg-success text-white rounded" style={{ padding: '4px 6px' }}>
      <button
        className="btn btn-light btn-sm d-flex align-items-center justify-content-center flex-shrink-0"
        onClick={() => {
          if (window.confirm("Haluatko varmasti poistua raporttinäkymästä?")) {
            navigate(homeRedirect);
            setMobileOpen(false);
          }
        }}
        title="Etusivu"
        style={{ padding: '2px 5px', height: 26 }}
      >
        <Home size={14} />
      </button>

      <Can permission={PERMISSIONS.REPORTS.EDIT}>
        <button
          className="btn btn-light btn-sm d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ padding: '2px 5px', height: 26 }}
          onClick={() => setShowSettings(!showSettings)}
          title="Asetukset"
        >
          <i className="bi bi-gear-fill" style={{ fontSize: '0.85rem' }}></i>
        </button>
      </Can>

      <span className="flex-grow-1" />

      <button
        onClick={() => setMobileOpen(false)}
        title="Sulje"
        style={{ background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: 4, padding: '2px 6px', height: 26, color: '#fff', cursor: 'pointer', flexShrink: 0, fontSize: '0.9rem', lineHeight: 1 }}
      >
        ✕
      </button>
    </div>

    {/* Report name */}
    <div style={{ fontSize: '0.68rem', fontWeight: 500, color: '#333', marginBottom: 6, paddingLeft: 4, letterSpacing: '0.01em', wordBreak: 'break-word' }}>
      {sidebarTitle}{isTemplateView && <span className="badge bg-danger ms-1" style={{ fontSize: '0.6rem' }}>POHJA</span>}
    </div>

    {/* Settings toggle below content */}
    {showSettings && (
      <div className="mb-2 px-2 d-flex flex-column gap-2">
        <label
          className="d-flex align-items-center justify-content-between p-2 border rounded"
          style={{ cursor: "pointer", backgroundColor: "#f8f9fa" }}
        >
          <span className="fw-semibold">Muokkausnäkymä</span>
          <input
            type="checkbox"
            className="form-check-input"
            checked={editMode}
            onChange={(e) => {
              setEditMode(e.target.checked);
              setShowSettings(false);
            }}
          />
        </label>
        <div className="p-2 border rounded d-flex flex-column gap-1" style={{ backgroundColor: "#f8f9fa" }}>
          <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>Raportin nimi</span>
          <div className="d-flex gap-1">
            <input type="text" className="form-control form-control-sm" value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()} />
            <button className="btn btn-success btn-sm flex-shrink-0" disabled={savingName || !nameInput.trim() || nameInput.trim() === report?.name}
              onClick={handleSaveName} title="Tallenna nimi">
              <i className="bi bi-check-lg"></i>
            </button>
          </div>
        </div>
        {!isTemplateView && !['julkisivujen_kuntotutkimus', 'julkisivujen_kuntotutkimus_v2', 'ikkuna_ovi_raportti'].includes(report?.type) && (
          <button
            className="btn btn-outline-success btn-sm d-flex align-items-center justify-content-center gap-2"
            onClick={() => {
              exportAllPTSToExcel({
                years: ptsContext.years, currentYear: ptsContext.currentYear,
                tekniikkaData: ptsContext.tekniikkaData, lviData: ptsContext.lviData,
                sahkoData: ptsContext.sahkoData, tutkimusData: ptsContext.tutkimusData,
                tekniikkaYhteensa: ptsContext.tekniikkaYhteensa, lviYhteensa: ptsContext.lviYhteensa,
                sahkoYhteensa: ptsContext.sahkoYhteensa, tutkimusYhteensa: ptsContext.tutkimusYhteensa,
                filename: ptsExcelFilename,
              });
              setShowSettings(false);
            }}
          >
            <i className="bi bi-file-earmark-excel"></i>
            Lataa PTS
          </button>
        )}
      </div>
    )}


    <div style={{ flexGrow: 1, overflowY: "auto" }}>
      {renderSidebar()}
    </div>


    {renderBottomButtons()}

  </div>

      {/* Desktop sidebar */}
      <div className="d-none d-md-flex flex-column" style={{ width: 280, height: "calc(100vh / 0.85)", position: "sticky", top: 0, backgroundColor: "#fff", zoom: 0.85 }}>
        <div className="mb-2 d-flex align-items-center justify-content-between bg-success text-white p-2 rounded">
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-light btn-sm d-flex align-items-center justify-content-center"
              onClick={() => {
                if (window.confirm("Haluatko varmasti poistua raporttinäkymästä?")) {
                  navigate(homeRedirect);
                }
              }}
              title="Etusivu"
              style={{ padding: "0.25rem" }}
            >
              <Home size={16} />
            </button>

            <span className="fw-bold">{sidebarTitle}</span>
            {isTemplateView && <span className="badge bg-danger ms-2">POHJA</span>}
          </div>
            <Can permission={PERMISSIONS.REPORTS.EDIT}>
              <button
                className="btn btn-light btn-sm rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                style={{ width: 32, height: 32 }}
                onClick={() => setShowSettings(!showSettings)}
                title="Asetukset"
              >
                <i className="bi bi-gear-fill" style={{ fontSize: "1rem" }}></i>
              </button>
            </Can>
        </div>

        {showSettings && (
          <div className="mb-2 px-2 d-flex flex-column gap-2">
            <label className="d-flex align-items-center justify-content-between p-2 border rounded" style={{ cursor: "pointer", backgroundColor: "#f8f9fa" }}>
              <span className="fw-semibold">Muokkausnäkymä</span>
              <input type="checkbox" className="form-check-input" checked={editMode} onChange={(e) => { setEditMode(e.target.checked); setShowSettings(false); }} />
            </label>
            <div className="p-2 border rounded d-flex flex-column gap-1" style={{ backgroundColor: "#f8f9fa" }}>
              <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>Raportin nimi</span>
              <div className="d-flex gap-1">
                <input type="text" className="form-control form-control-sm" value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()} />
                <button className="btn btn-success btn-sm flex-shrink-0" disabled={savingName || !nameInput.trim() || nameInput.trim() === report?.name}
                  onClick={handleSaveName} title="Tallenna nimi">
                  <i className="bi bi-check-lg"></i>
                </button>
              </div>
            </div>
            {!isTemplateView && !['julkisivujen_kuntotutkimus', 'julkisivujen_kuntotutkimus_v2', 'ikkuna_ovi_raportti'].includes(report?.type) && (
              <button
                className="btn btn-outline-success btn-sm d-flex align-items-center justify-content-center gap-2"
                onClick={() => {
                  exportAllPTSToExcel({
                    years: ptsContext.years, currentYear: ptsContext.currentYear,
                    tekniikkaData: ptsContext.tekniikkaData, lviData: ptsContext.lviData,
                    sahkoData: ptsContext.sahkoData, tutkimusData: ptsContext.tutkimusData,
                    tekniikkaYhteensa: ptsContext.tekniikkaYhteensa, lviYhteensa: ptsContext.lviYhteensa,
                    sahkoYhteensa: ptsContext.sahkoYhteensa, tutkimusYhteensa: ptsContext.tutkimusYhteensa,
                    filename: ptsExcelFilename,
                  });
                  setShowSettings(false);
                }}
              >
                <i className="bi bi-file-earmark-excel"></i>
                Lataa PTS
              </button>
            )}
          </div>
        )}

        <div style={{ flexGrow: 1, overflowY: "auto" }}>{renderSidebar()}</div>
        {renderBottomButtons()}
      </div>

          {showPdfSettingsModal && (
<div
  style={{
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3000,
  }}
>
  <div
    className="bg-white rounded-4 shadow-lg"
    style={{ width: 360, maxWidth: "90%", overflow: "hidden" }}
  >
    {/* Header */}
    <div
      className="d-flex align-items-center justify-content-between px-4 py-3"
      style={{ borderBottom: "1px solid #e9ecef", backgroundColor: "#f8f9fa" }}
    >
      <div className="d-flex align-items-center gap-2">
        <span style={{ fontSize: "1.2rem" }}>
          {pdfSettingsNextAction === "preview" ? "👁️" : "📄"}
        </span>
        <h6 className="mb-0 fw-semibold text-dark">
          {pdfSettingsNextAction === "preview" ? "Esikatselu" : "Lataa PDF"}
        </h6>
      </div>
      <button
        className="btn-close"
        onClick={() => setShowPdfSettingsModal(false)}
      />
    </div>

    {/* Body */}
    <div className="px-4 py-3">
      {pdfSettingsNextAction === "preview" ? (
        <div
          className="d-flex align-items-center justify-content-between p-3 rounded-3"
          style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0" }}
        >
          <div>
            <div className="fw-semibold text-dark" style={{ fontSize: "0.9rem" }}>
              Näytä kuvat
            </div>
            <div className="text-muted" style={{ fontSize: "0.90rem" }}>
              Kuvien lataus hidastaa esikatselua
            </div>
          </div>
          <div className="form-check form-switch mb-0">
            <input
              type="checkbox"
              className="form-check-input"
              id="previewImagesToggle"
              checked={showPreviewImages}
              onChange={(e) => setShowPreviewImages(e.target.checked)}
              style={{ width: "2.5em", height: "1.25em", cursor: "pointer" }}
            />
          </div>
        </div>
      ) : (
        <div
          className="d-flex align-items-center gap-2 p-3 rounded-3 text-muted"
          style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef", fontSize: "0.85rem" }}
        >
          <i className="bi bi-info-circle"></i>
          <span>PDF luodaan kaikilla kuvilla tiedostoksi.</span>
        </div>
      )}
    </div>

    {/* Footer */}
    <div
      className="d-flex justify-content-end gap-2 px-4 py-3"
      style={{ borderTop: "1px solid #e9ecef" }}
    >
      <button
        className="btn btn-outline-secondary btn-sm px-3"
        onClick={() => setShowPdfSettingsModal(false)}
      >
        Peruuta
      </button>
      <button
        className="btn btn-success btn-sm px-4 fw-semibold"
        onClick={() => {
          setShowPdfSettingsModal(false);
          if (pdfSettingsNextAction === "preview") handlePreviewPDF();
          else handleExportPDF();
        }}
      >
        {pdfSettingsNextAction === "preview" ? "Avaa esikatselu" : "Lataa PDF"}
      </button>
    </div>
  </div>
</div>
        )}

        {showPreviewModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '95vw' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Esikatselu</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowPreviewModal(false);
                  URL.revokeObjectURL(previewBlobUrl);
                  setPreviewBlobUrl(null);
                }}></button>
              </div>
              <div className="modal-body" style={{ height: '80vh' }}>
                {previewBlobUrl && (
                  <iframe
                    src={previewBlobUrl}
                    frameBorder="0"
                    style={{ width: '100%', height: '100%' }}
                    title="PDF Preview"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
<EmailModal
  open={showEmailModal}
  onClose={() => setShowEmailModal(false)}
  initialRecipients={emailInitialRecipients}
  subject={emailSubject}
  body={emailBody}
  pdfBase64={pdfBase64}
  filename={emailPdfFilename}
  card={rakennusData}
/>
        {loadingPDF && (
          <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: "1rem" }}>
            <div className="spinner-border text-light" style={{ width: "4rem", height: "4rem" }}></div>
            <p className="text-white mt-3 fs-5">{loadingMessage}</p>

            <div className="progress w-50 mt-2" style={{ height: "2rem" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${loadingProgress}%`, transition: "width 0.3s ease" }}
                aria-valuenow={loadingProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {loadingProgress > 5 ? `${loadingProgress}%` : ""}
              </div>
            </div>

            {!isTemplateView && kiinteistoId && (
              <div className="mt-3 px-3 py-2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.85rem' }}>
                <PdfReadyImagesChecker propertyId={kiinteistoId} />
              </div>
            )}
          </div>
        )}
        {pdfError && (() => {
          const { context, err } = pdfError;
          const details = [
            err?.message,
            err?.stack,
            err?.response?.data ? JSON.stringify(err.response.data, null, 2) : null,
          ].filter(Boolean).join('\n\n') || String(err);
          return (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '1rem' }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', maxWidth: 640, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div className="d-flex align-items-start justify-content-between gap-2">
                  <strong style={{ color: '#dc3545' }}>{context}</strong>
                  <button className="btn-close" onClick={() => setPdfError(null)} />
                </div>
                <textarea
                  readOnly
                  value={details}
                  onClick={e => e.target.select()}
                  style={{ fontFamily: 'monospace', fontSize: '0.78rem', resize: 'vertical', minHeight: 180, flex: 1, border: '1px solid #dee2e6', borderRadius: 4, padding: '0.5rem', background: '#f8f9fa', color: '#212529' }}
                />
                <div className="d-flex gap-2 justify-content-end">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => navigator.clipboard?.writeText(details)}>Kopioi</button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setPdfError(null)}>Sulje</button>
                </div>
              </div>
            </div>
          );
        })()}
    </>
  );
}
